import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getConfig } from "../../db/queries";
import { ESPECIALIDADE_LABEL, type EspecialidadeProf } from "../../types";
import {
  arquivarLocal,
  arquivarMembro,
  ativarAgenda,
  criarLocal,
  criarMembro,
  desativarAgenda,
  editarClinica,
  getClinicaAtiva,
  listarLocais,
  listarMembros,
} from "../queries";
import { PAPEL_LABEL, type PapelClinica } from "../types";

const ESPECIALIDADES: EspecialidadeProf[] = ["fono", "to", "psico", "nutri", "medico", "outro"];

export function ConfigAgenda() {
  const nav = useNavigate();
  const cfg = useLiveQuery(getConfig);
  const clinica = useLiveQuery(getClinicaAtiva);
  const clinicaId = clinica?.id;
  const membros = useLiveQuery(
    () => (clinicaId ? listarMembros(clinicaId) : Promise.resolve([])),
    [clinicaId],
  );
  const locais = useLiveQuery(
    () => (clinicaId ? listarLocais(clinicaId) : Promise.resolve([])),
    [clinicaId],
  );

  const [donoNome, setDonoNome] = useState("");

  // formulário de novo membro
  const [mNome, setMNome] = useState("");
  const [mPapel, setMPapel] = useState<PapelClinica>("profissional");
  const [mEsp, setMEsp] = useState<EspecialidadeProf>("fono");
  const [mReg, setMReg] = useState("");

  // formulário de novo local
  const [lNome, setLNome] = useState("");
  const [lEnd, setLEnd] = useState("");

  const ativa = Boolean(cfg?.agenda_ativa);

  // ── não ativada: porta de entrada da camada "contratável" ──────────────────
  if (cfg && !ativa) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav("/")}>← módulos</button>
        <h1 className="tela-titulo">agenda da clínica</h1>
        <p className="tela-sub">
          o calendário de consultas — opcional. enquanto não ativar, nada muda nos
          módulos de terapia.
        </p>

        <div className="card secao">
          <p style={{ margin: "0 0 var(--s3)" }}>
            ative para agendar, remarcar e acompanhar consultas. você começa como uma
            <strong> clínica de 1</strong>; depois pode adicionar colegas ou uma secretária.
          </p>
          <div className="campo">
            <label>seu nome (profissional)</label>
            <input
              value={donoNome}
              onChange={(e) => setDonoNome(e.target.value)}
              placeholder="ex.: Ana Lima"
            />
          </div>
          <button
            className="btn btn-primario btn-bloco btn-grande"
            onClick={async () => {
              await ativarAgenda(donoNome);
              nav("/agenda");
            }}
          >
            ativar agenda
          </button>
        </div>

        <p className="rel-rodape" style={{ border: "none" }}>
          tudo fica neste aparelho. confirmação por voz/WhatsApp (Synex) e Google Calendar
          chegam numa fase seguinte.
        </p>
      </main>
    );
  }

  async function adicionarMembro() {
    if (!clinicaId || !mNome.trim()) return;
    await criarMembro({
      clinica_id: clinicaId,
      nome: mNome,
      papel: mPapel,
      especialidade: mPapel === "profissional" ? mEsp : null,
      registro_classe: mReg || null,
    });
    setMNome("");
    setMReg("");
  }

  async function adicionarLocal() {
    if (!clinicaId || !lNome.trim()) return;
    await criarLocal(clinicaId, lNome, lEnd);
    setLNome("");
    setLEnd("");
  }

  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => nav("/agenda")}>← agenda</button>
      <h1 className="tela-titulo">configurar agenda</h1>

      {/* clínica */}
      <section className="secao">
        <div className="secao-titulo">clínica</div>
        <div className="card">
          <div className="campo" style={{ marginBottom: 0 }}>
            <label>nome da clínica</label>
            <input
              value={clinica?.nome ?? ""}
              onChange={(e) => clinicaId && editarClinica(clinicaId, { nome: e.target.value })}
            />
          </div>
        </div>
      </section>

      {/* equipe / membros */}
      <section className="secao">
        <div className="secao-titulo">equipe da clínica</div>
        {(membros ?? []).map((m) => (
          <div className="card linha-alimento" key={m.id} style={{ marginBottom: "var(--s2)" }}>
            <div className="foto" aria-hidden>👤</div>
            <div className="crescer">
              <div className="nome">{m.nome}</div>
              <div className="meta">
                {PAPEL_LABEL[m.papel]}
                {m.especialidade ? ` · ${ESPECIALIDADE_LABEL[m.especialidade]}` : ""}
                {m.registro_classe ? ` · ${m.registro_classe}` : ""}
              </div>
            </div>
            {(membros ?? []).length > 1 && (
              <button
                className="btn btn-fantasma"
                style={{ color: "var(--text-muted)" }}
                onClick={() => arquivarMembro(m.id)}
                aria-label={`remover ${m.nome}`}
              >
                remover
              </button>
            )}
          </div>
        ))}

        <div className="card" style={{ marginTop: "var(--s3)" }}>
          <div className="campo">
            <label>nome</label>
            <input value={mNome} onChange={(e) => setMNome(e.target.value)} placeholder="colega ou secretária" />
          </div>
          <div className="linha-campos">
            <div className="campo" style={{ marginBottom: 0 }}>
              <label>papel</label>
              <select value={mPapel} onChange={(e) => setMPapel(e.target.value as PapelClinica)}>
                <option value="profissional">profissional</option>
                <option value="secretaria">secretaria</option>
                <option value="admin">administração</option>
              </select>
            </div>
            {mPapel === "profissional" && (
              <div className="campo" style={{ marginBottom: 0 }}>
                <label>disciplina</label>
                <select value={mEsp} onChange={(e) => setMEsp(e.target.value as EspecialidadeProf)}>
                  {ESPECIALIDADES.map((e) => (
                    <option key={e} value={e}>{ESPECIALIDADE_LABEL[e]}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="campo" style={{ margin: "var(--s4) 0 0" }}>
            <label>registro de classe (opcional)</label>
            <input value={mReg} onChange={(e) => setMReg(e.target.value)} placeholder="CRFa / CREFITO / CRP / CRN" />
          </div>
          <button className="btn btn-bloco" style={{ marginTop: "var(--s3)" }} disabled={!mNome.trim()} onClick={adicionarMembro}>
            ➕ adicionar à equipe
          </button>
          <p className="meta" style={{ color: "var(--text-suave)", margin: "var(--s2) 0 0" }}>
            a secretaria só gere a agenda (nome + contato p/ confirmar) — nunca o prontuário.
          </p>
        </div>
      </section>

      {/* consultórios */}
      <section className="secao">
        <div className="secao-titulo">consultórios (opcional)</div>
        {(locais ?? []).map((l) => (
          <div className="card linha-alimento" key={l.id} style={{ marginBottom: "var(--s2)" }}>
            <div className="foto" aria-hidden>🏠</div>
            <div className="crescer">
              <div className="nome">{l.nome}</div>
              {l.endereco && <div className="meta">{l.endereco}</div>}
            </div>
            <button className="btn btn-fantasma" style={{ color: "var(--text-muted)" }} onClick={() => arquivarLocal(l.id)}>
              remover
            </button>
          </div>
        ))}
        <div className="card" style={{ marginTop: "var(--s3)" }}>
          <div className="campo">
            <label>nome</label>
            <input value={lNome} onChange={(e) => setLNome(e.target.value)} placeholder="ex.: sala 2" />
          </div>
          <div className="campo" style={{ marginBottom: 0 }}>
            <label>endereço (opcional)</label>
            <input value={lEnd} onChange={(e) => setLEnd(e.target.value)} />
          </div>
          <button className="btn btn-bloco" style={{ marginTop: "var(--s3)" }} disabled={!lNome.trim()} onClick={adicionarLocal}>
            ➕ adicionar consultório
          </button>
        </div>
      </section>

      {/* provedor */}
      <section className="secao">
        <div className="secao-titulo">provedor do calendário</div>
        <div className="card">
          <div className="seg" style={{ marginBottom: 0 }}>
            <button className="seg-opt ativo">nativo</button>
            <button className="seg-opt" disabled style={{ opacity: 0.5 }}>Google (em breve)</button>
          </div>
          <p className="meta" style={{ color: "var(--text-suave)", margin: "var(--s3) 0 0" }}>
            no nativo, o Degrau é a fonte da agenda. integrar ao Google Calendar que a
            clínica já usa é um próximo passo (Fase 2).
          </p>
        </div>
      </section>

      {/* desativar */}
      <section className="secao">
        <button
          className="btn btn-bloco"
          style={{ color: "var(--text-muted)" }}
          onClick={async () => {
            if (!confirm("desativar a agenda? os dados ficam guardados; ela só some da navegação.")) return;
            await desativarAgenda();
            nav("/");
          }}
        >
          desativar agenda
        </button>
      </section>
    </main>
  );
}
